import type { TextEncoding } from "./encodings";

// Where a byte sits in the document the editor holds. The dump is one line per row, every line padded to the same
// width, so the map between a byte offset and a position in the text is arithmetic rather than a search — which is
// what lets the page speak in bytes while CodeMirror speaks in characters, with nothing in between them to go wrong.

export const PER_ROW_OPTIONS = [8, 16, 24, 32];

// The gap every hex dump since `od` has put between groups of eight, which is what makes a byte countable by eye.
export const GROUP = 8;

// The column a byte's first digit sits at, counted from the start of its line: two digits and a space each, and one
// more space for every group boundary already passed.
export function columnOf(index: number): number {
  return index * 3 + Math.floor(index / GROUP);
}

// Every line is this wide, the short last one padded out to it, so the text column beside it starts at one x. It is
// the invariant `rowText` is written to rather than something the page reads, which is why only the test calls it.
export function lineWidth(perRow: number): number {
  return columnOf(perRow - 1) + 2;
}

export function rowCount(size: number, perRow: number): number {
  return Math.ceil(size / perRow);
}

export interface Spot {
  index: number;
  nibble: number;
}

// The byte a column lands on, and which half of it. A column in the gap after a byte belongs to that byte rather than
// to nothing, so a click between two bytes puts the caret on one instead of being swallowed.
export function spotAt(column: number, perRow: number): Spot {
  let index = 0;
  while (index + 1 < perRow && columnOf(index + 1) <= column) index++;
  return { index, nibble: column === columnOf(index) + 1 ? 1 : 0 };
}

// One line of the hex column, padded with spaces where the file has run out.
export function rowText(bytes: Uint8Array, row: number, perRow: number, upper: boolean): string {
  const table = upper ? UPPER : LOWER;
  const from = row * perRow;
  const parts: string[] = [];
  for (let index = 0; index < perRow; index++) {
    if (index > 0) parts.push(index % GROUP === 0 ? "  " : " ");
    const at = from + index;
    parts.push(at < bytes.length ? table[bytes[at]] : "  ");
  }
  return parts.join("");
}

// The whole document, a line to a row. It is the one thing here proportional to the file rather than to the screen,
// which is what the size ceiling in `source.ts` is set against — and it is handed over as lines rather than as one
// string, since joining a megabyte of them only to have the editor split it again is two passes for nothing.
export function dumpLines(bytes: Uint8Array, perRow: number, upper: boolean): string[] {
  const rows = rowCount(bytes.length, perRow);
  const lines: string[] = new Array(rows);
  for (let row = 0; row < rows; row++) lines[row] = rowText(bytes, row, perRow, upper);
  // A document with no lines at all is not one CodeMirror will take.
  return rows === 0 ? [""] : lines;
}

// One line of the text column, which is not in the document at all: it is drawn beside each line as a widget, so a
// selection over a run of bytes stays one unbroken range of hex rather than sweeping up the glyphs between the rows.
export function rowGlyphs(bytes: Uint8Array, row: number, perRow: number, encoding: TextEncoding): string {
  const from = row * perRow;
  let line = "";
  for (let index = 0; index < perRow; index++) {
    const at = from + index;
    line += at < bytes.length ? encoding.glyph(bytes[at]) ?? "." : " ";
  }
  return line;
}

// Padded to what the largest offset in this file needs, so the gutter is the same width top and bottom.
export function offsetDigits(size: number, base: number): number {
  const largest = Math.max(0, size - 1).toString(base).length;
  return base === 16 ? Math.max(4, largest + (largest % 2)) : largest;
}

export function formatOffset(offset: number, base: number, digits: number, upper: boolean): string {
  const spelled = offset.toString(base).padStart(digits, "0");
  return upper ? spelled.toUpperCase() : spelled;
}

export function formatByte(value: number, upper: boolean): string {
  return (upper ? UPPER : LOWER)[value];
}

// The two digits of every byte, worked out once: a dump of a megabyte asks for this four million times.
const LOWER = Array.from({ length: 256 }, (_, byte) => byte.toString(16).padStart(2, "0"));

const UPPER = LOWER.map((pair) => pair.toUpperCase());
